import { PudoTransactionVolumeByReceiverListChart, PudoTransactionVolumeBySenderListChart } from '@frontend/chart/components';
import { CommonMessage } from '@frontend/lang';
import { type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightClientListPagination } from '../insight-client-list-pagination.component';
import { InsightId } from '../insight-registry/insight-ids';
import { PudoTransactionUserGroup, PudoTransactionVolumeByUserItem, usePudoTransactionVolumeByUserList } from './use-pudo-transaction-volume-by-user-list';

const DETAIL_PAGE_SIZE = 25;

export interface PudoTransactionVolumeByUserRoleListProps {
    accountId: string;
    width?: InsightCardWidth;
    userGroup: PudoTransactionUserGroup;
    insightId: string;
    chartKey: string;
    title: ReactNode;
    subtitle: ReactNode;
}

const UserVolumeRows = ({ items }: { items: PudoTransactionVolumeByUserItem[] }) => (
    <>
        <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
            <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.USER.DEFAULT}</div>
            <div className='w-[120px] shrink-0 py-2 text-right'>{CommonMessage.OBJECTS.TRANSACTION.PLURAL}</div>
        </div>
        {items.map((item) => (
            <div
                key={item.label}
                className='border-border-default flex h-11 items-center gap-4 border-b'>
                <div className='text-foreground-default w-full min-w-0 truncate px-1 text-sm font-medium'>{item.label}</div>
                <div className='flex w-[120px] shrink-0 justify-end pe-1'>
                    <span className='text-background-surface bg-brand-muted inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                        {item.transactionCount}
                    </span>
                </div>
            </div>
        ))}
    </>
);

export const PudoTransactionVolumeByUserRoleList = ({
    accountId,
    width,
    userGroup,
    insightId,
    chartKey,
    title,
    subtitle
}: PudoTransactionVolumeByUserRoleListProps) => {
    const { items, isLoading, hasData, isDetailMode } = usePudoTransactionVolumeByUserList(accountId, userGroup);
    const [pageIndex, setPageIndex] = useState(0);

    useEffect(() => {
        setPageIndex(0);
    }, [items]);

    const pageCount = Math.max(1, Math.ceil(items.length / DETAIL_PAGE_SIZE));
    const safePageIndex = Math.min(pageIndex, pageCount - 1);
    const pageItems = isDetailMode ? items.slice(safePageIndex * DETAIL_PAGE_SIZE, safePageIndex * DETAIL_PAGE_SIZE + DETAIL_PAGE_SIZE) : items;

    return (
        <InsightCard
            insightId={insightId}
            width={width}
            chartKey={chartKey}
            title={title}
            subtitle={subtitle}
            isLoading={isLoading}>
            {!hasData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : isDetailMode ? (
                <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4'>
                    <div className='min-h-0 w-full min-w-0 flex-1 overflow-y-auto'>
                        <UserVolumeRows items={pageItems} />
                    </div>
                    <InsightClientListPagination
                        totalCount={items.length}
                        pageIndex={safePageIndex}
                        pageCount={pageCount}
                        onPageChange={setPageIndex}
                        navigationId='user-volume'
                    />
                </div>
            ) : (
                <div className='max-h-[420px] w-full min-w-0 overflow-y-auto'>
                    <UserVolumeRows items={pageItems} />
                </div>
            )}
        </InsightCard>
    );
};

export interface PudoTransactionVolumeBySenderListProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const PudoTransactionVolumeBySenderList = ({ accountId, width }: PudoTransactionVolumeBySenderListProps) => (
    <PudoTransactionVolumeByUserRoleList
        accountId={accountId}
        width={width}
        userGroup='sender'
        insightId={InsightId.PUDO_TRANSACTIONS_PER_SENDER}
        chartKey={PudoTransactionVolumeBySenderListChart.definition.key}
        title={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-volume-by-sender-list.top-senders-title'
                description='Title for the PuDo top-senders list on analytics insights'
                defaultMessage='Top senders'
            />
        }
        subtitle={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-volume-by-sender-list.subtitle'
                description='Subtitle indicating transactions per sender covers the last 30 days on the PuDo analytics insights tab'
                defaultMessage='last 30 days'
            />
        }
    />
);

export interface PudoTransactionVolumeByReceiverListProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const PudoTransactionVolumeByReceiverList = ({ accountId, width }: PudoTransactionVolumeByReceiverListProps) => (
    <PudoTransactionVolumeByUserRoleList
        accountId={accountId}
        width={width}
        userGroup='receiver'
        insightId={InsightId.PUDO_TRANSACTIONS_PER_RECEIVER}
        chartKey={PudoTransactionVolumeByReceiverListChart.definition.key}
        title={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-volume-by-receiver-list.top-receivers-title'
                description='Title for the PuDo top-receivers list on analytics insights'
                defaultMessage='Top receivers'
            />
        }
        subtitle={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-volume-by-receiver-list.subtitle'
                description='Subtitle indicating transactions per receiver covers the last 30 days on the PuDo analytics insights tab'
                defaultMessage='last 30 days'
            />
        }
    />
);
